import { useState, Fragment, useEffect, useMemo, useRef, type ComponentType, type SVGProps } from 'react'
import { Dialog, Transition, Menu } from '@headlessui/react'
import {
  Bars3Icon,
  HomeIcon,
  ExclamationCircleIcon,
  Cog6ToothIcon,
  XMarkIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  QueueListIcon,
  QuestionMarkCircleIcon,
  UsersIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  UserPlusIcon,
  ClipboardDocumentCheckIcon,
  SunIcon,
  MoonIcon,
  ChevronUpDownIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { Link, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { api } from '../lib/api'
import { useTheme } from '../context/ThemeContext'
import { useTenantContext, saveTenantSelection } from '../hooks/useTenantContext'
import { isDemoMode } from '../lib/demo/flag'
import { DemoBanner } from './DemoBanner'
import { DemoCta } from './DemoCta'
import { useGuardianDemoDriver } from '../lib/demo/mockDriver'

// ----------------------------------------------------------------------------
// Navigation model
// ----------------------------------------------------------------------------

type CountSeverity = 'admin' | 'beta'

interface NavigationItem {
  name: string
  href: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  adminOnly?: boolean
  count?: number
  countLabel?: string
  countSeverity?: CountSeverity
}

interface NavigationGroup {
  label: string
  items: NavigationItem[]
}

interface CurrentUserInfo {
  isGlobalAdmin: boolean
  email?: string
  displayName?: string
  role?: string
}

const navigationGroups: NavigationGroup[] = [
  {
    label: 'Posture',
    items: [
      { name: 'Command Center', href: '/dashboard', icon: HomeIcon },
      { name: 'Baseline Compliance', href: '/configuration-drift', icon: CheckCircleIcon },
      { name: 'Security Center', href: '/security', icon: ShieldCheckIcon },
      { name: 'User Analytics', href: '/user-insights', icon: UsersIcon },
    ],
  },
  {
    label: 'Reports',
    items: [
      { name: 'Executive Report', href: '/executive-report', icon: DocumentTextIcon },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        name: 'Tenant Management',
        href: '/tenants',
        icon: BuildingOffice2Icon,
        adminOnly: true,
        countLabel: 'Admin',
        countSeverity: 'admin',
      },
      {
        name: 'Tenant Onboarding',
        href: '/tenant-management',
        icon: UserPlusIcon,
        adminOnly: true,
        countLabel: 'Admin',
        countSeverity: 'admin',
      },
      {
        name: 'User Management',
        href: '/user-management',
        icon: UserGroupIcon,
        adminOnly: true,
        countLabel: 'Admin',
        countSeverity: 'admin',
      },
      {
        name: 'Admin Queue',
        href: '/admin/queue',
        icon: QueueListIcon,
        adminOnly: true,
        countLabel: 'Admin',
        countSeverity: 'admin',
      },
      {
        name: 'Scan Errors',
        href: '/admin/errors',
        icon: ExclamationCircleIcon,
        adminOnly: true,
        countLabel: 'Admin',
        countSeverity: 'admin',
      },
      {
        name: 'OPA Validation',
        href: '/admin/qa-validation',
        icon: ClipboardDocumentCheckIcon,
        adminOnly: true,
        countLabel: 'Admin',
        countSeverity: 'admin',
      },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
      { name: 'Help & Docs', href: '/help', icon: QuestionMarkCircleIcon },
    ],
  },
]

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function getInitials(value: string | null | undefined, fallback = 'GU'): string {
  if (!value) return fallback
  const parts = value.trim().split(/[\s.@_-]+/).filter(Boolean)
  if (parts.length === 0) return fallback
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function humanizeRole(role: string | null | undefined): string {
  if (!role) return ''
  return role.replace(/([a-z])([A-Z])/g, '$1 $2')
}

const PILL_CLASSES: Record<CountSeverity, string> = {
  admin: 'bg-white/5 text-slate-400 border border-white/10',
  beta: 'bg-secondaryBlue-300/15 text-secondaryBlue-300 border border-secondaryBlue-300/30',
}

function NavPill({
  severity,
  count,
  label,
}: {
  severity: CountSeverity
  count?: number
  label?: string
}) {
  const text = label ?? (typeof count === 'number' ? String(count) : '')
  if (!text) return null
  return (
    <span
      className={clsx(
        'text-[9px] font-bold tracking-[0.06em] uppercase rounded-full px-1.5 py-0.5 leading-none',
        PILL_CLASSES[severity],
      )}
    >
      {text}
    </span>
  )
}

// ----------------------------------------------------------------------------
// Tenant context block (sidebar)
// ----------------------------------------------------------------------------

function TenantContextBlock({ onSelect }: { onSelect?: () => void }) {
  const { defaultTenantId, userTenants, isGlobalAdmin, isLoading } = useTenantContext()

  // Resolve which tenant to display. For non-admins or admins with a stored
  // selection: show that tenant. For admins viewing "All Tenants": show the
  // aggregate label.
  const activeTenant = useMemo(() => {
    if (!defaultTenantId) return undefined
    return userTenants.find(t => t.tenantId === defaultTenantId)
  }, [defaultTenantId, userTenants])

  const handleSelect = (tenantId: string | undefined) => {
    saveTenantSelection(tenantId)
    onSelect?.()
    // Phase 12 decision: the per-page state owned by useTenantContext consumers
    // (Dashboard / Compliance / Security Center / User Analytics / etc.)
    // initializes from useTenantContext on mount. A full reload is the cleanest
    // way to propagate the change everywhere with zero risk of stale data.
    //
    // Lifting selection into a shared context that drives every page reactively
    // is feasible but touches every page's data-fetching layer. Reload here is
    // safe and consistent — keep until a future phase rewrites the data layer.
    window.location.reload()
  }

  const displayName = isLoading
    ? 'Loading…'
    : activeTenant
      ? activeTenant.name
      : isGlobalAdmin
        ? 'All Tenants'
        : (userTenants[0]?.name ?? 'No tenant')

  const subtitle = isLoading
    ? '—'
    : activeTenant
      ? activeTenant.domain || 'Tenant context'
      : isGlobalAdmin
        ? `${userTenants.length} accessible`
        : 'Tenant context'

  const initials = getInitials(displayName, 'GU')

  // Only render the dropdown when there's something to switch to.
  const showSwitcher = !isLoading && (isGlobalAdmin || userTenants.length > 1)

  const trigger = (
    <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center w-full px-4 py-3 text-left">
      <div className="w-[30px] h-[30px] rounded-full grid place-items-center bg-gradient-to-br from-primary-500 to-secondaryBlue-300 text-dark-900 font-extrabold text-xs">
        {initials}
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-slate-100 leading-tight truncate">{displayName}</div>
        <div className="text-[10px] uppercase tracking-[0.1em] font-semibold text-slate-400 mt-0.5 truncate">
          {subtitle}
        </div>
      </div>
      {showSwitcher && (
        <ChevronUpDownIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
      )}
    </div>
  )

  if (!showSwitcher) {
    // Single-tenant user: render as static block, no menu.
    return (
      <div className="mx-3.5 mt-3 mb-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
        {trigger}
      </div>
    )
  }

  return (
    <Menu as="div" className="relative mx-3.5 mt-3 mb-2">
      <Menu.Button
        className="w-full rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-colors"
        title="Switch tenant"
      >
        {trigger}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-auto rounded-xl bg-dark-800 py-1 shadow-dark-card border border-dark-600 focus:outline-none">
          {isGlobalAdmin && userTenants.length > 1 && (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => handleSelect(undefined)}
                  className={clsx(
                    'flex w-full items-center justify-between px-3 py-2 text-sm transition-colors',
                    active ? 'bg-primary-500/15 text-primary-300' : 'text-slate-200',
                    !defaultTenantId && 'font-semibold',
                  )}
                >
                  <span>All Tenants</span>
                  {!defaultTenantId && <CheckCircleIcon className="h-4 w-4 text-primary-400" />}
                </button>
              )}
            </Menu.Item>
          )}
          {userTenants.map(tenant => (
            <Menu.Item key={tenant.tenantId}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => handleSelect(tenant.tenantId)}
                  className={clsx(
                    'flex w-full items-center justify-between px-3 py-2 text-sm transition-colors',
                    active ? 'bg-primary-500/15 text-primary-300' : 'text-slate-200',
                    tenant.tenantId === defaultTenantId && 'font-semibold',
                  )}
                >
                  <span className="truncate">{tenant.name}</span>
                  {tenant.tenantId === defaultTenantId && (
                    <CheckCircleIcon className="h-4 w-4 text-primary-400 shrink-0 ml-2" />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

// ----------------------------------------------------------------------------
// Sidebar nav (group label + items)
// ----------------------------------------------------------------------------

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2.5 pb-2 flex items-center gap-2.5">
      <span className="text-[9px] font-bold tracking-[0.24em] uppercase text-slate-500">
        {children}
      </span>
      <span className="flex-1 h-px bg-white/[0.06]" aria-hidden="true" />
    </div>
  )
}

function SidebarNavItem({
  item,
  isActive,
  onClick,
}: {
  item: NavigationItem
  isActive: boolean
  onClick?: () => void
}) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={clsx(
        'group grid grid-cols-[20px_1fr_auto] gap-3.5 items-center px-3 py-2 mx-0 my-px rounded-lg text-[13px] font-medium transition-all',
        isActive
          ? 'bg-primary-500/[0.12] text-primary-400 font-semibold shadow-[inset_2px_0_0_rgb(var(--color-primary-500))]'
          : 'text-slate-300/70 hover:bg-white/[0.04] hover:text-slate-100',
      )}
    >
      <item.icon
        className={clsx(
          'h-[18px] w-[18px] shrink-0 transition-colors',
          isActive ? 'text-primary-400' : 'text-slate-500 group-hover:text-slate-200',
        )}
        aria-hidden="true"
      />
      <span className="truncate">{item.name}</span>
      {item.countSeverity && (
        <NavPill severity={item.countSeverity} count={item.count} label={item.countLabel} />
      )}
    </Link>
  )
}

function SidebarNav({
  groups,
  currentPath,
  onItemClick,
}: {
  groups: NavigationGroup[]
  currentPath: string
  onItemClick?: () => void
}) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 pt-2.5 pb-3.5 [scrollbar-width:thin]">
      {groups.map(group => (
        <div key={group.label} className="mb-5 last:mb-0">
          <GroupLabel>{group.label}</GroupLabel>
          <ul role="list" className="space-y-px">
            {group.items.map(item => (
              <li key={item.name}>
                <SidebarNavItem
                  item={item}
                  isActive={currentPath === item.href}
                  onClick={onItemClick}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  )
}

// ----------------------------------------------------------------------------
// Sidebar footer: user menu
// ----------------------------------------------------------------------------

function SidebarFooter({
  user,
  isGlobalAdmin,
  roleName,
}: {
  user: any
  isGlobalAdmin?: boolean
  roleName?: string
}) {
  // useMsal is only valid inside an MsalProvider — the demo build doesn't
  // mount one, so we no-op the menu's logout action there.
  const msal = isDemoMode ? null : useMsal()

  const handleLogout = () => {
    if (!msal) {
      // Demo: nothing to log out of. Take them back to iuvotech.com.
      window.location.href = 'https://iuvotech.com'
      return
    }
    msal.instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
      mainWindowRedirectUri: window.location.origin,
    })
  }

  const displayName = user?.name || user?.username || 'Guardian User'
  const role = humanizeRole(roleName)
  const initials = getInitials(displayName)

  return (
    <div className="p-3 border-t border-white/[0.06]">
      {/* User menu */}
      {user ? (
        <Menu as="div" className="relative">
          <Menu.Button className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/[0.04] transition-colors">
            <div className="w-[30px] h-[30px] rounded-full bg-dark-700 text-slate-100 grid place-items-center font-bold text-xs">
              {initials}
            </div>
            <div className="text-left min-w-0">
              <div className="text-xs font-semibold text-slate-100 leading-tight truncate">{displayName}</div>
              {role && (
                <div className="text-[10px] uppercase tracking-[0.1em] font-semibold text-slate-500 truncate">
                  {role}
                </div>
              )}
            </div>
            <ChevronUpDownIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute bottom-full left-0 right-0 z-50 mb-2 origin-bottom-left rounded-xl bg-dark-800 py-2 shadow-dark-card border border-dark-600 focus:outline-none">
              <div className="px-3 py-2 text-sm border-b border-dark-700">
                <div className="font-medium text-slate-100">{displayName}</div>
                <div className="truncate text-slate-500 text-xs">{user.username || user.homeAccountId}</div>
                {isGlobalAdmin && (
                  <span className="inline-block mt-1 text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">
                    Global Admin
                  </span>
                )}
              </div>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/help"
                    className={clsx(
                      active ? 'bg-dark-700 text-primary-400' : 'text-slate-300',
                      'flex w-full items-center px-3 py-2 text-sm transition-colors',
                    )}
                  >
                    <QuestionMarkCircleIcon className="mr-3 h-5 w-5" aria-hidden="true" />
                    Help
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <a
                    href="https://github.com/iuvonauts/guardian-feedback/issues"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={clsx(
                      active ? 'bg-dark-700 text-primary-400' : 'text-slate-300',
                      'flex w-full items-center px-3 py-2 text-sm transition-colors',
                    )}
                  >
                    <ChatBubbleLeftRightIcon className="mr-3 h-5 w-5" aria-hidden="true" />
                    Send Feedback
                  </a>
                )}
              </Menu.Item>
              <div className="my-1 border-t border-dark-700" />
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={clsx(
                      active ? 'bg-dark-700 text-red-400' : 'text-slate-300',
                      'flex w-full items-center px-3 py-2 text-sm transition-colors',
                    )}
                  >
                    <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" aria-hidden="true" />
                    Sign out
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      ) : null}
    </div>
  )
}

// ----------------------------------------------------------------------------
// Sidebar surface (shared between desktop and mobile)
// ----------------------------------------------------------------------------

function SidebarBody({
  groups,
  currentPath,
  user,
  isGlobalAdmin,
  roleName,
  onNavigate,
}: {
  groups: NavigationGroup[]
  currentPath: string
  user: any
  isGlobalAdmin?: boolean
  roleName?: string
  onNavigate?: () => void
}) {
  return (
    <div
      data-sidebar="true"
      className="flex h-full flex-col bg-dark-800 border-r border-white/[0.06] overflow-hidden"
      style={{
        backgroundImage:
          'radial-gradient(ellipse 90% 30% at 50% -10%, rgb(var(--color-primary-500) / 0.08), transparent 60%)',
      }}
    >
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/[0.06]">
        <img
          src="/iuvo-icon.png"
          alt="iuvo Guardian"
          className="h-8 w-8"
          style={{ filter: 'drop-shadow(0 0 8px rgb(var(--color-primary-500) / 0.5))' }}
        />
        <div className="flex flex-col">
          <span className="text-[15px] font-extrabold leading-tight tracking-tight">
            <span
              className="text-primary-500"
              style={{ filter: 'drop-shadow(0 0 10px rgb(var(--color-primary-500) / 0.5))' }}
            >
              iuvo
            </span>
            {' '}
            <span className="text-slate-100">Guardian</span>
          </span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold mt-0.5">
            M365 Posture Platform
          </span>
        </div>
      </div>

      {/* Tenant context block — sole tenant switcher on routed pages
          (Phase 12). Per-page <TenantSelector> instances on Posture pages
          have been removed. The legacy unrouted pages (BaselineReports,
          ComplianceReports, ConfigAsCode, StandardsCompliance) still import
          TenantSelector but they're dead code — their functionality moved
          into the BaselineCompliance / SecurityCenter tabs. */}
      <TenantContextBlock onSelect={onNavigate} />

      {/* Nav groups */}
      <SidebarNav groups={groups} currentPath={currentPath} onItemClick={onNavigate} />

      {/* Footer: user menu */}
      <SidebarFooter user={user} isGlobalAdmin={isGlobalAdmin} roleName={roleName} />
    </div>
  )
}

// ----------------------------------------------------------------------------
// Top app bar: breadcrumbs + actions
// ----------------------------------------------------------------------------

const ROUTE_TO_GROUP: Array<{ test: (path: string) => boolean; group: string; label: string }> = [
  { test: p => p === '/dashboard' || p === '/', group: 'Posture', label: 'Command Center' },
  { test: p => p.startsWith('/configuration-drift') || p.startsWith('/drift'), group: 'Posture', label: 'Baseline Compliance' },
  { test: p => p.startsWith('/security'), group: 'Posture', label: 'Security Center' },
  { test: p => p.startsWith('/user-insights'), group: 'Posture', label: 'User Analytics' },
  { test: p => p.startsWith('/executive-report'), group: 'Reports', label: 'Executive Report' },
  { test: p => p === '/tenants', group: 'Administration', label: 'Tenants' },
  { test: p => p.startsWith('/tenant-management'), group: 'Administration', label: 'Tenant Onboarding' },
  { test: p => p.startsWith('/user-management'), group: 'Administration', label: 'User Management' },
  { test: p => p.startsWith('/admin/queue'), group: 'Administration', label: 'Admin Queue' },
  { test: p => p.startsWith('/admin/errors'), group: 'Administration', label: 'Scan Errors' },
  { test: p => p.startsWith('/admin/qa-validation'), group: 'Administration', label: 'OPA Validation' },
  { test: p => p.startsWith('/settings'), group: 'Workspace', label: 'Settings' },
  { test: p => p.startsWith('/help'), group: 'Workspace', label: 'Help & Docs' },
]

function deriveBreadcrumbs(pathname: string): { group: string; label: string } {
  const match = ROUTE_TO_GROUP.find(r => r.test(pathname))
  if (match) return { group: match.group, label: match.label }
  // Fallback: prettify the first non-empty path segment.
  const first = pathname.split('/').filter(Boolean)[0] ?? 'Dashboard'
  const pretty = first
    .split(/[-_]/)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')
  return { group: 'Workspace', label: pretty }
}

function AppBreadcrumbs() {
  const location = useLocation()
  const { group, label } = deriveBreadcrumbs(location.pathname)
  return (
    <nav className="flex items-center gap-2 text-[13px] font-semibold" aria-label="Breadcrumb">
      <Link to="/dashboard" className="text-slate-500 hover:text-slate-200 transition-colors">
        iuvo Guardian
      </Link>
      <ChevronRightIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden="true" />
      <span className="text-slate-500">{group}</span>
      <ChevronRightIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden="true" />
      <span className="text-text-primary font-bold">{label}</span>
    </nav>
  )
}

function TopBarIconButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick?: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="relative h-9 w-9 grid place-items-center rounded-lg text-slate-500 hover:text-slate-100 hover:bg-dark-700 light:text-slate-600 light:hover:text-slate-900 light:hover:bg-slate-100 transition-colors"
    >
      {children}
    </button>
  )
}

// ----------------------------------------------------------------------------
// Layout
// ----------------------------------------------------------------------------

interface Props {
  children: React.ReactNode
}

export default function Layout({ children }: Props) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentUserInfo, setCurrentUserInfo] = useState<CurrentUserInfo | null>(null)
  const [isLoadingUser, setIsLoadingUser] = useState(true)
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  // useMsal is only valid inside an MsalProvider — the demo build doesn't
  // mount one, so we no-op here and use a synthetic "current user."
  const { accounts } = isDemoMode ? { accounts: [] as any[] } : useMsal()
  const { theme, toggleTheme } = useTheme()

  // Reset scroll on route changes. The scroll container is <main> (not window),
  // so browser scroll restoration doesn't apply. Keyed on pathname only —
  // ?tab=... changes within Security Center / Baseline Compliance keep position.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 })
  }, [location.pathname])

  const currentUser = isDemoMode
    ? { name: 'Jane Doe', username: 'jane.doe@contoso.onmicrosoft.com' }
    : accounts[0] || null

  // Drives simulated real-time KPI ticks in the demo. Hook is no-op in production.
  useGuardianDemoDriver()

  // Fetch current user info from API. Mirrors the previous Layout behavior:
  // secure-by-default if the call fails (treat user as non-admin).
  useEffect(() => {
    const abortController = new AbortController()

    const fetchCurrentUser = async () => {
      try {
        if (currentUser) {
          const userInfo = await api.getCurrentUser()
          if (!abortController.signal.aborted) {
            setCurrentUserInfo(userInfo)
          }
        }
      } catch (error) {
        if (abortController.signal.aborted) return
        console.error('Failed to fetch current user info:', error)
        setCurrentUserInfo({ isGlobalAdmin: false })
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingUser(false)
        }
      }
    }

    fetchCurrentUser()

    return () => {
      abortController.abort()
    }
  }, [currentUser])

  const isGlobalAdmin = currentUserInfo?.isGlobalAdmin || false

  // Demo-mode hidden items: anything that needs a real backend / multi-tenant
  // surface or settings that don't apply in a public sample. Names match
  // navigationGroups[].items[].name above.
  const DEMO_HIDDEN_NAV = new Set(['Tenant Management', 'Settings'])

  // Filter admin-only items. While loading, hide them to avoid a flash of
  // protected links — secure by default. In demo mode also strip the
  // demo-hidden names so visitors don't see options that 404.
  const visibleGroups: NavigationGroup[] = useMemo(() => {
    return navigationGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          if (isDemoMode) {
            if (item.adminOnly) return false
            if (DEMO_HIDDEN_NAV.has(item.name)) return false
            return true
          }
          if (!item.adminOnly) return true
          if (isLoadingUser) return false
          return isGlobalAdmin
        }),
      }))
      .filter(group => group.items.length > 0)
  }, [isLoadingUser, isGlobalAdmin])

  // Pre-warm the tenants query so the sidebar block renders immediately
  // without a fetch waterfall when the user changes routes.
  useQuery({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  return (
    <div className="flex h-screen">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-dark-900/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-slate-400" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                <SidebarBody
                  groups={visibleGroups}
                  currentPath={location.pathname}
                  user={currentUser}
                  isGlobalAdmin={isGlobalAdmin}
                  roleName={currentUserInfo?.role}
                  onNavigate={() => setSidebarOpen(false)}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-[252px] lg:flex-col">
        <SidebarBody
          groups={visibleGroups}
          currentPath={location.pathname}
          user={currentUser}
          isGlobalAdmin={isGlobalAdmin}
          roleName={currentUserInfo?.role}
        />
      </div>

      <div className="lg:pl-[252px] flex flex-col flex-1">
        {/* App-level top bar: breadcrumb-led, with actions */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-9 py-3.5 bg-dark-800/85 light:bg-white/95 backdrop-blur-xl border-b border-dark-700 light:border-slate-200">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile menu trigger */}
            <button
              type="button"
              className="lg:hidden -m-2 p-2 text-slate-400 hover:text-slate-200 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <AppBreadcrumbs />
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/help"
              title="Help & Docs"
              aria-label="Help & Docs"
              className="relative h-9 w-9 grid place-items-center rounded-lg text-slate-500 hover:text-slate-100 hover:bg-dark-700 light:text-slate-600 light:hover:text-slate-900 light:hover:bg-slate-100 transition-colors"
            >
              <QuestionMarkCircleIcon className="h-[18px] w-[18px]" />
            </Link>
            <TopBarIconButton
              label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={toggleTheme}
            >
              {theme === 'dark' ? (
                <SunIcon className="h-[18px] w-[18px]" />
              ) : (
                <MoonIcon className="h-[18px] w-[18px]" />
              )}
            </TopBarIconButton>
          </div>
        </header>

        {/* Main content */}
        <main ref={mainRef} className="flex-1 overflow-auto">
          <div className="px-4 py-8 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
      <DemoBanner />
      <DemoCta />
    </div>
  )
}
